import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { FileText, Scale, CreditCard, AlertTriangle, Ban, RefreshCw, Gavel, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";

const sections = [
  {
    icon: FileText,
    title: "Acceptance of Terms",
    content: [
      "By accessing or using the Xentic Solutions website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations.",
      "If you do not agree with any of these terms, you are prohibited from using or accessing our services.",
      "These terms apply to all visitors, users, and clients of Xentic Solutions.",
      "We reserve the right to modify these terms at any time. Continued use of our services constitutes acceptance of any changes.",
    ],
  },
  {
    icon: Scale,
    title: "Services Description",
    content: [
      "Xentic Solutions provides AI chatbot automation, website development, SEO optimization, workflow automation, lead generation systems, and social media management services.",
      "Our AI chatbot setup includes custom training, deployment, and integration with your existing platforms within 72 to 120 hours of project approval.",
      "All services are provided on a best-effort basis. While we strive for optimal results, specific outcomes such as lead increases or conversion rates are not guaranteed.",
      "Service specifications, features, and deliverables will be outlined in individual service agreements or proposals provided before project commencement.",
    ],
  },
  {
    icon: CreditCard,
    title: "Pricing & Payment",
    content: [
      "Our standard AI chatbot setup fee is a one-time payment of $150, which includes deployment, custom training, and 3 months of complimentary support.",
      "After the initial 3-month period, an optional Support Manager service is available at $50 per month. This is entirely optional and includes dedicated bot maintenance and optimization.",
      "All payments are processed securely through our approved payment providers. Prices are in USD unless otherwise stated.",
      "We do not require long-term contracts. You may cancel the optional monthly support service at any time without penalty.",
    ],
  },
  {
    icon: RefreshCw,
    title: "Money-Back Guarantee",
    content: [
      "We offer a 30-day money-back guarantee on our AI chatbot setup service. If you are not satisfied with the service within the first 30 days after deployment, you may request a full refund of the setup fee.",
      "Refund requests must be submitted in writing to info@xenticsolutions.com within 30 days of your chatbot going live.",
      "The guarantee applies to the one-time setup fee only. Monthly support fees, if applicable, are non-refundable for the period in which they were charged.",
      "Refunds will be processed within 10 business days of approval.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "Limitation of Liability",
    content: [
      "Xentic Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.",
      "Our total liability for any claim arising from these terms or our services shall not exceed the amount you paid to us in the 12 months preceding the claim.",
      "We are not responsible for any loss of data, revenue, or business opportunities resulting from service interruptions, technical issues, or third-party platform changes.",
      "We do not guarantee uninterrupted or error-free operation of AI chatbots, as performance may be affected by factors outside our control.",
    ],
  },
  {
    icon: Ban,
    title: "Prohibited Uses",
    content: [
      "You may not use our services for any unlawful purpose or in violation of any local, state, national, or international law.",
      "You may not use our AI chatbots to distribute spam, misleading information, or harmful content.",
      "You may not attempt to reverse-engineer, decompile, or disassemble any of our proprietary technology or AI training models.",
      "You may not resell, sublicense, or redistribute our services without prior written consent from Xentic Solutions.",
    ],
  },
  {
    icon: Gavel,
    title: "Governing Law",
    content: [
      "These Terms of Service shall be governed by and construed in accordance with the laws of the State of Illinois, without regard to its conflict of law provisions.",
      "Any disputes arising from these terms or our services shall be resolved through binding arbitration in DuPage County, Illinois.",
      "If any provision of these terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.",
    ],
  },
  {
    icon: Mail,
    title: "Contact Us",
    content: [
      "For any questions regarding these Terms of Service, please contact us at info@xenticsolutions.com.",
      "You may also reach us by phone at +1 (872) 297-0607 or visit us at 107 Williamsburg Lane, Bolingbrook, IL 60440.",
      "We aim to respond to all inquiries within 2 business days.",
    ],
  },
];

const TermsOfService = () => (
  <Layout>
    <SEOHead
      title="Terms of Service | Xentic Solutions"
      description="Read the Terms of Service for Xentic Solutions. Understand your rights and responsibilities when using our AI automation services."
      path="/terms-of-service"
    />

    {/* Hero */}
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-blob" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/20 rounded-full blur-[120px] animate-blob-delay" />
      <div className="container relative z-10 text-center">
        <ScrollReveal>
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-primary/20">
            <Gavel className="h-4 w-4" />
            Legal Agreement
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
            Terms of <span className="text-gradient">Service</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Please read these terms carefully before using Xentic Solutions services. By using our services, you agree to these terms.
          </p>
          <p className="text-sm text-muted-foreground/60 mt-4">
            Last updated: March 1, 2026
          </p>
        </ScrollReveal>
      </div>
    </section>

    {/* Content */}
    <section className="py-16">
      <div className="container max-w-4xl space-y-8">
        {sections.map((section, i) => (
          <ScrollReveal key={i} delay={i * 0.05}>
            <motion.div whileHover={{ scale: 1.005 }} transition={{ type: "spring", stiffness: 400, damping: 30 }}>
              <Card className="overflow-hidden group hover:border-primary/30 transition-colors duration-300">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors duration-300">
                      <section.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-heading text-xl md:text-2xl font-bold mb-4">{section.title}</h2>
                      <ul className="space-y-3">
                        {section.content.map((item, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </ScrollReveal>
        ))}
      </div>
    </section>

    {/* Bottom Note */}
    <section className="py-12 section-alt">
      <div className="container max-w-2xl text-center">
        <ScrollReveal>
          <p className="text-sm text-muted-foreground leading-relaxed">
            By using Xentic Solutions services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you have any questions, please do not hesitate to contact us.
          </p>
        </ScrollReveal>
      </div>
    </section>
  </Layout>
);

export default TermsOfService;
